import React, { useMemo } from 'react';
import { calculateGPPerUnit, calculateGrossProfit } from '../../bidAnalysisFormulas';
import classes from '../../bidAnalysisTemplate.module.scss';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NCFiveYearSummaryTableProps {
  /** yearFormData[packIndex][yearIndex] — persisted per-year data */
  yearFormData: Record<number, Record<number, any>>;
  /** Base pack data keyed by packIndex */
  packSizeFormData: Record<number, any>;
  /** Live form data for the currently active pack + year */
  formData: any;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  allPackSizeTabs: Array<{ index: number; conceptName?: string; packSize?: string; label?: string }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const YEAR_COUNT = 5;

const COLUMN_LABELS: string[] = [
  'Base Year',
  'OY1',
  'OY2',
  'OY3',
  'OY4',
  '5-Year Total',
];

const ROW_DEFS: Array<{ key: string; label: string; format: 'currency' | 'percent' | 'num' }> = [
  { key: 'revenue',     label: 'Contract Annual Revenue', format: 'currency' },
  { key: 'gp',         label: 'Contract Annual GP',      format: 'currency' },
  { key: 'usage',      label: 'Annual Usage',            format: 'num'      },
  { key: 'grossProfit',label: 'Gross Profit %',          format: 'percent'  },
  { key: 'gpPerUnit',  label: 'GP / Unit',               format: 'currency' },
];

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  if (isNaN(value)) return '$0.00';
  return `$${value.toFixed(2)}`;
}

function formatPercent(value: number): string {
  if (isNaN(value)) return '0.00%';
  return `${value.toFixed(2)}%`;
}

function formatNum(value: number): string {
  if (isNaN(value)) return '0.00';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatValue(value: number, format: 'currency' | 'percent' | 'num'): string {
  switch (format) {
    case 'currency': return formatCurrency(value);
    case 'percent':  return formatPercent(value);
    case 'num':      return formatNum(value);
  }
}

// ---------------------------------------------------------------------------
// Per-year aggregation (mirrors the logic in calculate5YearSummary.ts and
// the existing calculatedValues useMemo in NCGlobalOutputSection, using only
// props already available to NCGlobalOutputSection)
// ---------------------------------------------------------------------------

interface YearSummary {
  revenue: number;
  gp: number;
  usage: number;
  grossProfit: number;
  gpPerUnit: number;
}

function aggregateYear(
  yearIndex: number,
  allPackSizeTabs: NCFiveYearSummaryTableProps['allPackSizeTabs'],
  packSizeFormData: Record<number, any>,
  yearFormData: Record<number, Record<number, any>>,
  formData: any,
  activePackSizeTab: number | null,
  activeYearTab: number | null,
): YearSummary {
  let revenue = 0;
  let gp = 0;
  let usage = 0;

  allPackSizeTabs.forEach(packTab => {
    const baseData = packSizeFormData[packTab.index] || {};
    const yearData = yearFormData[packTab.index]?.[yearIndex] || {};
    const isActive =
      activePackSizeTab === packTab.index && activeYearTab === yearIndex;

    // Use live formData for the active cell so keystrokes are reflected
    // immediately without waiting for blur — same strategy as the existing
    // calculatedValues useMemo in NCGlobalOutputSection.
    const packData = isActive
      ? { ...baseData, ...yearData, ...formData }
      : { ...baseData, ...yearData };

    const packUsage =
      parseFloat(packData.ncEstAnnualUsage || packData.targetAnnualUsage || '0') || 0;
    const bidPrice =
      parseFloat(packData.suggestedBidPrice || packData.suggested_bid_price || '0') || 0;
    const gsmsProfitRaw = packData.gsmsProfit ?? packData.gsms_profit;
    const hasExplicitProfit =
      gsmsProfitRaw !== undefined && gsmsProfitRaw !== null && gsmsProfitRaw !== '';
    const profit = hasExplicitProfit ? parseFloat(String(gsmsProfitRaw)) || 0 : null;

    let packRevenue: number;
    let packGP: number;

    if (packUsage > 0 && bidPrice > 0) {
      packRevenue = packUsage * bidPrice;
      if (profit !== null) {
        packGP = profit !== 0 ? packRevenue * (profit / 100) : 0;
      } else {
        packGP = parseFloat(packData.contractAnnualGP || '0') || 0;
      }
    } else {
      packRevenue = parseFloat(packData.contractAnnualRevenue || '0') || 0;
      packGP = parseFloat(packData.contractAnnualGP || '0') || 0;
    }

    if (!isNaN(packRevenue)) revenue += packRevenue;
    if (!isNaN(packGP)) gp += packGP;
    if (!isNaN(packUsage)) usage += packUsage;
  });

  return {
    revenue,
    gp,
    usage,
    grossProfit: calculateGrossProfit(revenue, gp),
    gpPerUnit: calculateGPPerUnit(gp, usage),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const NCFiveYearSummaryTable: React.FC<NCFiveYearSummaryTableProps> = ({
  yearFormData,
  packSizeFormData,
  formData,
  activePackSizeTab,
  activeYearTab,
  allPackSizeTabs,
}) => {
  const yearSummaries = useMemo<YearSummary[]>(() => {
    return Array.from({ length: YEAR_COUNT }, (_, yearIndex) =>
      aggregateYear(
        yearIndex,
        allPackSizeTabs,
        packSizeFormData,
        yearFormData,
        formData,
        activePackSizeTab,
        activeYearTab,
      ),
    );
  }, [yearFormData, packSizeFormData, formData, allPackSizeTabs, activePackSizeTab, activeYearTab]);

  const grandTotal = useMemo<YearSummary>(() => {
    const totalRevenue = yearSummaries.reduce((sum, y) => sum + y.revenue, 0);
    const totalGP = yearSummaries.reduce((sum, y) => sum + y.gp, 0);
    const totalUsage = yearSummaries.reduce((sum, y) => sum + y.usage, 0);
    return {
      revenue: totalRevenue,
      gp: totalGP,
      usage: totalUsage,
      grossProfit: calculateGrossProfit(totalRevenue, totalGP),
      gpPerUnit: calculateGPPerUnit(totalGP, totalUsage),
    };
  }, [yearSummaries]);

  // Column data: 5 per-year summaries + the grand total column
  const allColumns: YearSummary[] = [...yearSummaries, grandTotal];

  return (
    <div
      className={classes.globalTableWrapper}
      style={{ marginTop: '2rem' }}
    >
      <table className={classes.globalTable}>
        <thead>
          <tr>
            {/* Row label column */}
            <th style={{ minWidth: '200px', whiteSpace: 'nowrap', textAlign: 'left' }}>
              Metric
            </th>
            {COLUMN_LABELS.map(label => (
              <th
                key={label}
                style={{
                  minWidth: '140px',
                  whiteSpace: 'nowrap',
                  // Grand total column gets a slightly distinct header to make it stand out
                  ...(label === '5-Year Total'
                    ? { background: 'rgba(255,255,255,0.15)', fontWeight: 800 }
                    : {}),
                }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROW_DEFS.map(rowDef => (
            <tr key={rowDef.key}>
              <td
                style={{
                  fontWeight: 600,
                  color: '#4d2e6d',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {rowDef.label}
              </td>
              {allColumns.map((colData, colIdx) => {
                const value = colData[rowDef.key as keyof YearSummary] as number;
                const isGrandTotal = colIdx === allColumns.length - 1;
                return (
                  <td
                    key={colIdx}
                    style={{
                      fontWeight: isGrandTotal ? 700 : 400,
                      ...(isGrandTotal
                        ? {
                            background: '#f0e8f8',
                            color: '#4d2e6d',
                            borderLeft: '2px solid #c4a8e0',
                          }
                        : {}),
                    }}
                  >
                    {formatValue(value, rowDef.format)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
